import { getInvoicingPageUrl, PurchaseInvoiceImportSource } from '@constants'
import { useBusinessContext } from '@context'
import { useTheme } from '@hooks'
import { ICombinedInvoice } from '@query'
import { formatDate } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaReceipt, FaTag } from 'react-icons/fa'
import { getInvoiceGrossTotal } from '@pages/InvoicingPage/invoiceAmounts'
import { PurchaseInvoiceListIcon, SalesInvoiceListIcon } from '@pages/InvoicingPage/invoiceIcons'
import {
  CombinedStatusIcon,
  getCombinedInvoiceStatus,
  getCombinedPurchaseInvoiceStatus
} from './combinedStatus'
import { GeneralListItem } from './GeneralListItem'
import { RepetitionStatusIcon } from './RepetitionStatus'
import {
  DebtCollectionIcon,
  SoftReminderListIcon
} from '@pages/InvoicingPage/InvoiceStatusBadge.tsx'
import { postDueFromInvoice } from '@pages/InvoicingPage/postDueUi.ts'
import { TextTooltip } from '@components'
import styled from 'styled-components'
import { ITag } from '@root/query/tags'

interface InvoiceListItemProps {
  combinedInvoice: ICombinedInvoice
  isSelected: boolean
  isFirst: boolean
  isLast: boolean
  tagsById: Map<number, ITag>
}

export const InvoiceListItem: React.FC<InvoiceListItemProps> = ({
  combinedInvoice,
  isSelected,
  isFirst,
  isLast,
  tagsById
}) => {
  const [t] = useTranslation()
  const theme = useTheme()
  const { slug } = useBusinessContext()
  const tagIds = combinedInvoice.item.tag_ids
  const tags = tagIds.map(tagId => tagsById.get(tagId)).filter(Boolean) as ITag[]
  const tagIcons =
    tags.length > 0 ? (
      <TagDotsContainer>
        {tags.map(tag => (
          <FaTag
            key={`tag-icon-${combinedInvoice.type}-${combinedInvoice.item.id}-${tag.id}`}
            size={12}
            color={tag.color}
          />
        ))}
      </TagDotsContainer>
    ) : null

  if (combinedInvoice.type === 'sales') {
    const invoice = combinedInvoice.item
    const ui = postDueFromInvoice(invoice)

    return (
      <GeneralListItem
        to={getInvoicingPageUrl(slug, { id: invoice.id, type: combinedInvoice.type })}
        icon={<SalesInvoiceListIcon />}
        iconColorHex={theme.colors.primary}
        isFirst={isFirst}
        isLast={isLast}
        isSelected={isSelected}
        date={formatDate(invoice.invoicing_date)}
        statusDot={[
          ...(invoice?.is_recurrence_active
            ? [<RepetitionStatusIcon key={`status-icon-repetition-${invoice.id}`} />]
            : []),
          ...(ui.reminderMark
            ? [
                <SoftReminderListIcon
                  key={`status-icon-reminder-${invoice.id}`}
                  failed={ui.reminderMark === 'failed'}
                />
              ]
            : []),
          ...(ui.kraviaMark
            ? [<DebtCollectionIcon key={`status-icon-debt-collection-${invoice.id}`} />]
            : []),
          <CombinedStatusIcon
            key={`status-icon-${invoice.id}`}
            {...getCombinedInvoiceStatus(invoice)}
          />,
          ...(tagIcons ? [tagIcons] : [])
        ]}
        contact={invoice.receiver_info?.name}
        amount={getInvoiceGrossTotal(invoice.total_amount, invoice.total_vat_amount)}
      />
    )
  }

  if (combinedInvoice.type === 'purchase') {
    const invoice = combinedInvoice.item
    const isExpenseClaim = invoice.import_source === PurchaseInvoiceImportSource.EXPENSE_CLAIM

    return (
      <GeneralListItem
        to={getInvoicingPageUrl(slug, { id: invoice.id, type: combinedInvoice.type })}
        icon={<PurchaseInvoiceListIcon />}
        iconColorHex={theme.colors.warning}
        isFirst={isFirst}
        isLast={isLast}
        isSelected={isSelected}
        statusDot={[
          ...(isExpenseClaim
            ? [
                <TextTooltip
                  key={`expense-claim-${invoice.id}`}
                  tooltip={t('invoicing.purchaseInvoiceDetails.expenseClaimListTooltip')}
                >
                  <ExpenseClaimListMark>
                    <FaReceipt size={12} color={theme.colors.primary} />
                  </ExpenseClaimListMark>
                </TextTooltip>
              ]
            : []),
          <CombinedStatusIcon
            key={`status-icon-${invoice.id}`}
            {...getCombinedPurchaseInvoiceStatus(invoice, { includePaid: true })}
          />,
          ...(tagIcons ? [tagIcons] : [])
        ]}
        date={formatDate(invoice.invoicing_date)}
        contact={invoice.sender_name}
        amount={-invoice.amount}
      />
    )
  }

  return null
}

const TagDotsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const ExpenseClaimListMark = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`
